import { imageModels } from "@fitness-app/data-models/ai/models";
import { imageModelTasks } from "@fitness-app/data-models/ai/tasks";
// TRE-1614 — model pricing for AI cost attribution. Rates are USD per 1,000,000 tokens, taken from
// each provider's published API pricing (verified July 2026 — see the epic/commit for sources).
//
// Keys are matched as a substring of the model id (longest key wins), so a family prefix (e.g.
// "gpt-5.6") covers unlisted variants. A model with no matching entry logs its token counts with
// costUsd = null (never a fabricated cost), so a new model just needs a row here. Cache-read tokens
// default to 10% of the input rate (the ~90% prompt-cache discount is universal across OpenAI,
// Anthropic and Google); override per-model with `cachedInput` only where it differs.

interface ModelRate {
  /** USD per 1M input tokens. */
  input: number;
  /** USD per 1M output tokens. */
  output: number;
  /** USD per 1M cached-input (cache-read) tokens; defaults to 10% of `input` when omitted. */
  cachedInput?: number;
}

const MODEL_RATES: Record<string, ModelRate> = {
  // OpenAI GPT-6 family — verified 2026-10-02 against https://developers.openai.com/api/docs/models
  "gpt-6-astra": { input: 10, output: 50 },
  "gpt-6.1-sol": { input: 2, output: 10, cachedInput: 0.1 }, // cache reads at 5%, not 10%
  "gpt-6-sol": { input: 2, output: 10 },
  "gpt-6-luna": { input: 0.1, output: 0.5 },
  // OpenAI GPT-5.6 tiers — repriced since the July launch (verified 2026-10-02); Sol's rate is a
  // promotion "at least through November 21, 2026", the gpt-5.6 alias routes to Sol
  "gpt-5.6-sol": { input: 4, output: 20 },
  "gpt-5.6-terra": { input: 2, output: 12 },
  "gpt-5.6-luna": { input: 0.2, output: 1.2 },
  "gpt-5.6": { input: 4, output: 20 },
  // OpenAI GPT-5.5 (flagship)
  "gpt-5.5": { input: 5, output: 30, cachedInput: 0.5 },
  // OpenAI GPT-5.4
  "gpt-5.4-mini": { input: 0.75, output: 4.5 },
  "gpt-5.4": { input: 2.5, output: 15 },
  // Google Gemini
  "gemini-3.8-flash": { input: 1.5, output: 7.5 },
  "gemini-3.1-flash-lite": { input: 0.25, output: 1.5 },
  "gemini-3.6-flash": { input: 1.5, output: 7.5 },
  // NOTE: 3.5 Flash is 3× the price of 3-flash-preview — it is NOT a drop-in upgrade
  // (verified August 2026 against https://ai.google.dev/gemini-api/docs/pricing).
  "gemini-3.5-flash-lite": { input: 0.3, output: 2.5 },
  "gemini-3.5-flash": { input: 1.5, output: 9 },
  "gemini-3-flash": { input: 0.5, output: 3 }, // covers gemini-3-flash-preview / gemini-3.0-flash
  "gemini-3.1-pro": { input: 2, output: 12 }, // ≤200K context tier
  "gemini-3-pro": { input: 2, output: 12 }, // covers gemini-3-pro-preview / gemini-3.0-pro
  "gemini-2.5-flash": { input: 0.3, output: 2.5 },
  "gemini-2.5-pro": { input: 1.25, output: 10 },
  // Google Gemini image models (Nano Banana). A generated image is billed as OUTPUT tokens at a
  // rate an order of magnitude above the text one, so these keys MUST stay longer than their text
  // counterparts — "gemini-3.1-flash-lite-image" has to win over "gemini-3.1-flash-lite", or every
  // generated image would be priced 20× too low. Verified August 2026 against
  // https://ai.google.dev/gemini-api/docs/pricing.
  "gemini-3.1-flash-lite-image": { input: 0.25, output: 30 }, // Nano Banana 2 Lite — ~$0.034 / 1K image
  "gemini-3.1-flash-image": { input: 0.5, output: 60 }, // Nano Banana 2 — ~$0.067 / 1K image
  "gemini-3-pro-image": { input: 2, output: 120 }, // Nano Banana Pro — ~$0.134 / 1K image
  "gemini-2.5-flash-image": { input: 0.3, output: 30 }, // Nano Banana 1 (legacy) — ~$0.039 / 1K image (1290 tokens)
  // Anthropic Claude
  "claude-opus-5-5": { input: 4, output: 20 },
  // Ceny do 100K tokenów promptu ($0.50 / $2.50 powyżej). Bez tego wpisu „claude-haiku” niżej
  // liczyłby Haiku 5.5 po stawce 4.5, dziesięć razy za drogo.
  "claude-haiku-5-5": { input: 0.1, output: 0.5 },
  "claude-opus": { input: 5, output: 25 },
  "claude-sonnet": { input: 3, output: 15 },
  "claude-haiku": { input: 1, output: 5 },
  claude: { input: 3, output: 15 }, // fallback = Sonnet tier
};

// https://ai.google.dev/gemini-api/docs/latest-model?hl=en — verified 2026-09-05.
const GEMINI_38_PROMOTION_END = new Date("2027-01-01T00:00:00Z");
const GEMINI_38_INTRODUCTORY_RATE: ModelRate = { input: 0.75, output: 3.75 };

const resolveRate = (model: string, at: Date): ModelRate | null => {
  const normalized = model.toLowerCase();
  const key = Object.keys(MODEL_RATES)
    .filter((candidate) => normalized.includes(candidate))
    .sort((a, b) => b.length - a.length)[0];
  if (key === "gemini-3.8-flash" && at < GEMINI_38_PROMOTION_END) return GEMINI_38_INTRODUCTORY_RATE;
  return key ? (MODEL_RATES[key] ?? null) : null;
};

/**
 * Output tokens Google bills for one generated 1K image (≈1MP, 1024×1024) — the size every one of
 * our image prompts asks for. Used only as a fallback: the provider's reported usage wins whenever
 * the SDK gives us one.
 */
export const IMAGE_OUTPUT_TOKENS_1K = 1120;

/**
 * Model obrazkowy do masowej generacji zdjęć potraw (nano banana 2 lite) — najtańszy z rodziny.
 * Mieszka tutaj, a nie w tasku, bo panel musi pokazać szacowany koszt PRZED uruchomieniem, a nie
 * ma po co ciągnąć całego `@fitness-app/ai` do bundla tylko po nazwę modelu.
 */
export const DISH_IMAGE_MODEL = imageModels[imageModelTasks.dishImage.default].model;

/**
 * Best-effort USD cost of one model call. Returns null (not 0) for an unpriced model so the caller
 * can log token usage without a made-up cost. Cache-read tokens are billed at the model's cached
 * rate and are assumed to be part of `inputTokens` (so they are subtracted from the full-price input).
 */
export const estimateModelCostUsd = ({
  model,
  inputTokens,
  outputTokens,
  cacheReadTokens = 0,
  at = new Date(),
}: {
  model: string;
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens?: number;
  /** Date of generation, for time-limited provider rates. */
  at?: Date;
}): number | null => {
  const rate = resolveRate(model, at);
  if (!rate) {
    return null;
  }

  const cachedRate = rate.cachedInput ?? rate.input * 0.1;
  const fullPriceInput = Math.max(0, inputTokens - cacheReadTokens);
  const perMillion =
    (fullPriceInput * rate.input + cacheReadTokens * cachedRate + outputTokens * rate.output) / 1_000_000;

  return Math.round(perMillion * 1_000_000) / 1_000_000;
};

/**
 * USD cost of generating `images` pictures with an image model. Prefers the usage the provider
 * actually reported; when the SDK returns nothing usable (Gemini occasionally omits usage on an
 * image-only response) it falls back to the published per-image token count, so a batch always
 * reports a cost rather than silently summing zeros.
 */
export const estimateImageGenerationCostUsd = ({
  model,
  images,
  inputTokens = 0,
  outputTokens,
}: {
  model: string;
  images: number;
  inputTokens?: number;
  outputTokens?: number;
}): number | null =>
  estimateModelCostUsd({
    model,
    inputTokens,
    outputTokens: outputTokens && outputTokens > 0 ? outputTokens : images * IMAGE_OUTPUT_TOKENS_1K,
  });
